/**
 * Phase 2L-A29 — Product / Service Detail Pages content map.
 *
 * One entry per public route under `/products/{slug}` or `/services/{slug}`.
 * Content is intentionally version-controlled (no admin editing yet — see PRD).
 *
 * Wording guard rails:
 *   ✅ "helps reduce risk", "supports better water quality", "designed to assist"
 *   ❌ "guaranteed safe water", health certifications, NATA wording
 *
 * Images: reference public PWS-hosted images by URL (already public on
 * pristinewatersystems.com.au). No re-hosting.
 */

const PWS_IMG = (path) => `https://pristinewatersystems.com.au/wp-content/uploads/${path}`;

const PAGES = {
  // ============================================================== PRODUCTS
  "products/tr20uv": {
    kind: "product",
    name: "Protect-UV TR20UV — Twin 20\" Rural Filtration + UV",
    tagline:
      "Whole-of-home filtration with UV disinfection, sized for rural tank-water households.",
    hero_image: PWS_IMG("2022/08/Twin_UV_system_poly_tank_Protect-UV_logo-1024x768_1280x854.jpeg"),
    what_it_does: [
      "Filters tank water through sediment and carbon stages before passing it through a UV disinfection chamber.",
      "Designed to help reduce microbiological risk from rainwater tanks and ground-water supplies where pre-filtration and water clarity conditions are suitable.",
      "Treats every tap in the home — kitchen, bathroom, laundry — not just one filtered outlet.",
    ],
    best_for: [
      "Rural homes on rainwater tank or bore-water supply",
      "Households where bacteria has been detected and ongoing protection is wanted",
      "Properties with higher daily flow demand (multiple bathrooms / large households)",
      "Homes wanting a chemical-free alternative to chlorine dosing",
    ],
    included: [
      "Twin 20\" x 4.6\" stainless steel housings",
      "Sediment pre-filter + carbon block cartridges",
      "Stainless-steel UV reactor chamber (AS/NZS 4348 + AS/NZS 4397 approved unit)",
      "Quartz sleeve + UV lamp",
      "Ballast / power supply",
      "Professional installation by a qualified PWS technician",
    ],
    benefits: [
      "UV inactivates 99.99% of bacteria, viruses and protozoa passing through the chamber",
      "No chlorine, no chemicals, no taste taint",
      "Higher flow rate than the standard T20UV — suited to rural demand",
      "Backed by ongoing annual servicing for stable long-term performance",
    ],
    maintenance: [
      "UV lamp replacement every 12 months (see Annual service)",
      "Filter cartridge replacement every ~12 months depending on water quality",
      "Periodic quartz-sleeve clean during annual service",
    ],
    related: [
      { label: "T20UV (standard variant)", to: "/products/t20uv" },
      { label: "Annual service & filter change", to: "/services/annual-service" },
      { label: "How UV sterilisation works", to: "/services/uv-sterilisation" },
    ],
    disclaimer:
      "UV does not treat chemical contaminants and does not replace tank cleaning, retesting, or other corrective action where required. UV effectiveness depends on adequate pre-filtration and water clarity.",
  },

  "products/t20uv": {
    kind: "product",
    name: "Protect-UV T20UV — Twin 20\" Filtration + UV",
    tagline:
      "Standard whole-of-home filtration with UV disinfection for smaller households or lower demand.",
    hero_image: PWS_IMG("2023/06/3_Twin_Kit-min-scaled.jpg"),
    what_it_does: [
      "Same filtration + UV approach as the TR20UV but sized for households with lower flow demand.",
      "Helps reduce microbiological risk from rainwater or tank-stored water by treating every tap in the home.",
      "Suited to smaller properties, town-water homes with secondary tank supply, or single-bathroom homes.",
    ],
    best_for: [
      "Smaller households or holiday homes on tank water",
      "Town-water homes with a secondary tank supply",
      "Properties where the TR20UV's higher flow rate is not required",
      "Homes wanting a chemical-free alternative to chlorine dosing",
    ],
    included: [
      "Twin 20\" stainless steel housings",
      "Sediment pre-filter + carbon block cartridges",
      "Stainless-steel UV reactor chamber",
      "Quartz sleeve + UV lamp",
      "Ballast / power supply",
      "Professional installation by a qualified PWS technician",
    ],
    benefits: [
      "UV inactivates 99.99% of bacteria, viruses and protozoa passing through the chamber",
      "Whole-of-home protection — every tap, not just one outlet",
      "Lower cost than the TR20UV where rural flow rates aren't required",
      "Compatible with the same annual service schedule as the TR20UV",
    ],
    maintenance: [
      "UV lamp replacement every 12 months",
      "Filter cartridge replacement every ~12 months",
    ],
    related: [
      { label: "TR20UV (rural variant)", to: "/products/tr20uv" },
      { label: "T20 — filtration only, no UV", to: "/products/filtration-twin" },
      { label: "How UV sterilisation works", to: "/services/uv-sterilisation" },
    ],
    disclaimer:
      "UV does not treat chemical contaminants and does not replace tank cleaning, retesting, or other corrective action where required.",
  },

  "products/filtration-twin": {
    kind: "product",
    name: "T20 — Twin 20\" Filtration (no UV)",
    tagline:
      "Whole-of-home sediment and taste/odour filtration without UV. A lower-cost filtration-only option.",
    hero_image: PWS_IMG("2023/06/3_Twin_Kit-min-scaled.jpg"),
    what_it_does: [
      "Filters incoming water through sediment and carbon stages, helping reduce particulate, taste and odour issues.",
      "Does NOT provide UV disinfection. Not suitable as a microbiological treatment where bacteria has been detected.",
      "Best paired with a UV system if microbiological risk reduction is also required.",
    ],
    best_for: [
      "Town-water homes wanting taste / chlorine reduction",
      "Properties wanting basic whole-home filtration only",
      "Households where UV will be added in a later stage",
      "Where budget is the primary constraint and bacteria has NOT been detected",
    ],
    included: [
      "Twin 20\" housings with sediment + carbon cartridges",
      "Professional installation by a qualified PWS technician",
      "UV-ready: a UV chamber can be added later as a post-installation upgrade",
    ],
    benefits: [
      "Significantly reduces sediment, chlorine taste and odour",
      "Whole-home coverage at a lower price point than UV systems",
      "Upgradeable to T20UV later if needs change",
    ],
    maintenance: [
      "Filter cartridge replacement every ~12 months",
    ],
    related: [
      { label: "T20UV (with UV disinfection)", to: "/products/t20uv" },
      { label: "TR20UV (rural variant)", to: "/products/tr20uv" },
      { label: "Annual filter change", to: "/services/filter-change" },
    ],
    disclaimer:
      "Filtration alone does not inactivate bacteria or viruses. Where microbiological risk reduction is required, a UV system is recommended.",
  },

  // ============================================================== SERVICES
  "services/biocleanse": {
    kind: "service",
    name: "BioCleanse — Pipework Flushing & Hygiene Treatment",
    tagline:
      "Supplementary treatment that helps address biofilm and stagnation inside household pipework.",
    hero_image: PWS_IMG("2017/03/img3.jpg"),
    what_it_does: [
      "Flushes a controlled sanitising treatment through the home's internal pipework to help address biofilm, stagnation and pipework hygiene concerns.",
      "Targets the pipework downstream of the tank — somewhere a filter or UV system cannot reach on its own.",
      "Recommended in combination with tank cleaning and/or UV installation, not as a replacement.",
    ],
    best_for: [
      "Homes where bacteria has been detected and pipework hygiene is suspected",
      "Properties that have sat vacant for extended periods",
      "Pre-rental / pre-sale water-quality preparation",
      "Following a tank-cleaning service for end-to-end hygiene",
    ],
    included: [
      "Flushing of household pipework with PWS-approved sanitiser",
      "Onsite technician time and disposal of flush water",
      "Basic before/after onsite checks",
    ],
    benefits: [
      "Supports better water quality at the tap, not just at the tank",
      "Helps reduce risk where biofilm has built up in older or under-used pipework",
      "Pairs cleanly with QuadClean tank cleaning + Protect-UV filtration",
    ],
    maintenance: [
      "Typically a one-off treatment unless underlying issues (e.g. ongoing contamination, unaddressed catchment problems) remain.",
    ],
    related: [
      { label: "QuadClean tank cleaning", to: "/services/tank-cleaning" },
      { label: "Protect-UV TR20UV", to: "/products/tr20uv" },
    ],
    disclaimer:
      "BioCleanse does not replace UV disinfection, tank cleaning, filtration, retesting, or other corrective action where required.",
  },

  "services/tank-cleaning": {
    kind: "service",
    name: "Tank Cleaning — QuadClean™ Process",
    tagline:
      "Professional rainwater tank cleaning using the PWS 4-step QuadClean™ process.",
    hero_image: PWS_IMG("2017/04/Rural_Tanks.jpg"),
    what_it_does: [
      "Removes sediment and organic sludge from the bottom of the tank without draining it.",
      "Aerates and circulates the remaining water to restore healthy oxygen distribution top-to-bottom.",
      "Applies the AqueousPro™ sanitiser — approved under the Australian Drinking Water Guidelines — to control bacteria and viruses.",
      "Finishes with a basic onsite water test (pH and conductivity) and a written report.",
    ],
    best_for: [
      "Rainwater tanks that haven't been serviced in 2+ years",
      "Households experiencing taste, odour or discolouration issues",
      "Properties where catchment debris has built up in the tank",
      "Post-flood or post-storm tank recovery",
    ],
    included: [
      "Step 1 — Sediment removal: sludge vacuumed and pumped to waste with minimal water loss",
      "Step 2 — Circulation & aeration: oxygenates the remaining water through the tank",
      "Step 3 — Sanitisation: AqueousPro™ applied to control bacteria, fungi and viruses",
      "Step 4 — Water test: pH + TDS check with results recorded and sent to the owner",
    ],
    benefits: [
      "Minimal water loss — no need to fully drain the tank",
      "No chlorine, no harsh chemicals",
      "Supports better water quality at the source, before filtration",
      "Restores tank hygiene as a foundation for any downstream UV or filtration system",
    ],
    maintenance: [
      "Recommended every 2–3 years for typical residential tanks, or sooner where catchment issues persist.",
    ],
    related: [
      { label: "QuadClean — Poly / steel tanks", to: "/services/quadclean-poly" },
      { label: "QuadClean — Concrete tanks", to: "/services/quadclean-concrete" },
      { label: "First flush diverter (catchment upgrade)", to: "/services/first-flush-diverter" },
    ],
    disclaimer:
      "Tank cleaning improves the source water but does not replace filtration, UV disinfection, retesting, or other corrective action where required.",
  },

  "services/quadclean-poly": {
    kind: "service",
    variant: "poly",
    name: "QuadClean™ — Poly / Steel Tank Cleaning",
    tagline:
      "The QuadClean™ 4-step process applied to poly and steel rainwater tanks.",
    hero_image: PWS_IMG("2022/10/QuadClean%E2%84%A2-Pristine-Water-Systems-Water-Storage-Tank-Cleaning-Filtration-Treatment.jpg"),
    what_it_does: [
      "Vacuum-removes sediment, sludge and organic matter from the tank floor.",
      "Aerates and circulates the remaining water without draining the tank.",
      "Sanitises with AqueousPro™ — approved under the Australian Drinking Water Guidelines.",
      "Finishes with an onsite pH + TDS test and a written summary.",
    ],
    best_for: [
      "Poly (plastic) and Colorbond / steel tanks",
      "Typical residential rainwater tanks up to ~25,000 L",
      "Tanks with accessible top inlet",
    ],
    included: [
      "Sediment vacuum & sludge removal",
      "Tank water aeration & circulation",
      "AqueousPro™ sanitisation",
      "Final pH + TDS water check",
      "Onsite summary report",
    ],
    benefits: [
      "Minimal water loss — no full drain required",
      "Restores tank hygiene without harsh chemicals",
      "Recommended foundation step before installing UV / filtration",
    ],
    maintenance: [
      "Recommended every 2–3 years, or sooner where the catchment is heavily contaminated.",
    ],
    related: [
      { label: "QuadClean — Concrete tanks", to: "/services/quadclean-concrete" },
      { label: "Protect-UV TR20UV", to: "/products/tr20uv" },
      { label: "First flush diverter", to: "/services/first-flush-diverter" },
    ],
    disclaimer:
      "Pricing may vary depending on tank size, access and site conditions. Tank cleaning does not replace filtration, UV or retesting where required.",
  },

  "services/quadclean-concrete": {
    kind: "service",
    variant: "concrete",
    name: "QuadClean™ — Concrete Tank Cleaning",
    tagline:
      "The QuadClean™ 4-step process applied to concrete in-ground or above-ground tanks.",
    hero_image: PWS_IMG("2017/04/SludgeRemoval1.jpg"),
    what_it_does: [
      "Vacuum-removes sediment, sludge and organic matter from the tank floor.",
      "Aerates and circulates the remaining water without fully draining the tank.",
      "Sanitises with AqueousPro™ — approved under the Australian Drinking Water Guidelines.",
      "Includes additional access / inspection time appropriate for concrete tank construction.",
    ],
    best_for: [
      "Concrete in-ground or above-ground rainwater tanks",
      "Older property tank installations where concrete is the original tank material",
      "Larger residential or small-commercial tanks",
    ],
    included: [
      "Sediment vacuum & sludge removal",
      "Tank water aeration & circulation",
      "AqueousPro™ sanitisation",
      "Final pH + TDS water check",
      "Onsite summary report",
      "Extended onsite time appropriate for concrete tank access",
    ],
    benefits: [
      "Same QuadClean process as poly tanks, adjusted for concrete construction",
      "Minimal water loss — no full drain required",
      "Supports better water quality from the source before any filtration",
    ],
    maintenance: [
      "Recommended every 2–3 years, more frequently if the catchment is heavily contaminated.",
    ],
    related: [
      { label: "QuadClean — Poly / steel tanks", to: "/services/quadclean-poly" },
      { label: "Protect-UV TR20UV", to: "/products/tr20uv" },
      { label: "First flush diverter", to: "/services/first-flush-diverter" },
    ],
    disclaimer:
      "Pricing may vary depending on tank size, access and site conditions. Tank cleaning does not replace filtration, UV or retesting where required.",
  },

  "services/uv-sterilisation": {
    kind: "service",
    name: "UV Sterilisation — How It Works",
    tagline:
      "Plain-English explainer of how UV disinfection helps reduce microbiological risk.",
    hero_image: PWS_IMG("2018/09/UV-System-Diagram.jpg"),
    what_it_does: [
      "Passes water through a stainless-steel chamber containing a UV lamp.",
      "UV light at 254 nm damages the DNA of bacteria, viruses and protozoa, inactivating them so they can no longer reproduce.",
      "No chemicals added, no taste taint, no by-products.",
    ],
    best_for: [
      "Homes wanting microbiological protection without chlorine",
      "Households on rainwater tank or bore-water supply",
      "Properties where bacteria has been detected and an ongoing safeguard is wanted",
    ],
    included: [
      "Stainless-steel UV chamber (AS/NZS 4348 + AS/NZS 4397 approved)",
      "Quartz sleeve protecting the lamp",
      "Replaceable UV lamp (typically 12-month service life)",
      "Ballast / power supply with safety cut-off",
    ],
    benefits: [
      "Helps inactivate 99.99% of bacteria, viruses and parasites passing through the chamber",
      "Immediate treatment — no contact time required",
      "Environmentally friendly — no chemical residue, no chloramines or trihalomethanes",
      "Low ongoing cost — one lamp + one filter set per year",
    ],
    maintenance: [
      "UV lamp replacement every 12 months",
      "Quartz sleeve inspection / clean during annual service",
      "Pre-filter cartridge changes alongside the lamp",
    ],
    related: [
      { label: "Protect-UV TR20UV (rural)", to: "/products/tr20uv" },
      { label: "Protect-UV T20UV (standard)", to: "/products/t20uv" },
      { label: "Annual service", to: "/services/annual-service" },
    ],
    disclaimer:
      "UV light is harmful to eyes and exposed skin — never look at a UV lamp when switched on. UV is not a treatment for chemical contaminants and depends on adequate pre-filtration and water clarity to be effective.",
  },

  "services/annual-service": {
    kind: "service",
    name: "Annual Service — UV Lamp & System Check",
    tagline:
      "Yearly service visit to keep your UV system running as designed.",
    hero_image: PWS_IMG("2023/06/6_Twin_UV-min-scaled.jpg"),
    what_it_does: [
      "Replaces the UV lamp at the recommended 12-month interval.",
      "Inspects the quartz sleeve and cleans it where required.",
      "Checks ballast / power supply operation and sterilisation chamber integrity.",
      "Replaces pre-filter cartridges (separately quoted if not bundled).",
    ],
    best_for: [
      "Households on a Protect-UV TR20UV or T20UV system",
      "Properties where year-round microbiological protection is required",
    ],
    included: [
      "New UV lamp installed",
      "Quartz sleeve inspection / clean",
      "System sanitisation",
      "Onsite operating check & sign-off",
    ],
    benefits: [
      "Keeps UV performance at design specification",
      "Catches preventative issues (ballast wear, sleeve fouling) before they become failures",
      "Supports ongoing maintenance compliance for the installed system",
    ],
    maintenance: [
      "Annual cadence is the manufacturer recommendation. Heavier-duty systems may require shorter cycles.",
    ],
    related: [
      { label: "Annual filter change", to: "/services/filter-change" },
      { label: "How UV sterilisation works", to: "/services/uv-sterilisation" },
    ],
    disclaimer:
      "Annual servicing helps maintain UV system performance but does not certify the safety of the water itself.",
  },

  "services/filter-change": {
    kind: "service",
    name: "Annual Filter Change",
    tagline:
      "Scheduled replacement of sediment and carbon cartridges in your filtration system.",
    hero_image: PWS_IMG("2023/06/3_Twin_Kit-min-scaled.jpg"),
    what_it_does: [
      "Replaces the sediment + carbon cartridges in your Twin 20\" filtration system.",
      "Sanitises the housings before reassembly.",
      "Onsite operating check after refitting.",
    ],
    best_for: [
      "Any home on a PWS Twin 20\" filtration system (T20, T20UV or TR20UV)",
      "Households that haven't changed cartridges in ~12 months or longer",
    ],
    included: [
      "Replacement cartridges (sediment + carbon)",
      "Housing sanitisation",
      "Onsite technician time",
    ],
    benefits: [
      "Restores filter flow rate after cartridge fouling",
      "Helps maintain UV effectiveness by keeping pre-filtration working as designed",
      "Pairs cleanly with the annual UV lamp replacement",
    ],
    maintenance: [
      "Every 12 months for typical residential tank water. Sooner where water carries heavy sediment.",
    ],
    related: [
      { label: "Annual UV service", to: "/services/annual-service" },
      { label: "T20UV / TR20UV systems", to: "/products/tr20uv" },
    ],
    disclaimer:
      "Cartridge type and quantity may vary depending on the installed system and water quality.",
  },

  "services/first-flush-diverter": {
    kind: "service",
    name: "First Flush Diverter",
    tagline:
      "Catchment upgrade that diverts the first volume of rainwater away from the tank.",
    hero_image: PWS_IMG("2017/04/Town_Tank_LP.jpg"),
    what_it_does: [
      "Fits into the downpipe between the roof and the tank.",
      "Diverts the first rinse-off of each rain event — the volume most likely to carry roof contaminants — away from the tank.",
      "Refills automatically and is ready for the next rain event.",
    ],
    best_for: [
      "Rural and suburban tank-water homes",
      "Properties under tree canopy or near dust / traffic",
      "Households wanting to reduce roof-contaminant load on a freshly cleaned tank",
    ],
    included: [
      "Diverter unit sized to the catchment",
      "Installation into the downpipe",
      "Setup and operating check",
    ],
    benefits: [
      "Supports better water quality at the source",
      "Helps reduce roof contaminants entering stored water",
      "Extends the time between tank cleans by reducing sediment accumulation",
    ],
    maintenance: [
      "Periodic check that the diverter is draining and resetting correctly. Clean the screen / outlet as needed.",
    ],
    related: [
      { label: "Catchment upgrade overview", to: "/services/catchment-upgrade" },
      { label: "QuadClean tank cleaning", to: "/services/tank-cleaning" },
    ],
    disclaimer:
      "A diverter reduces but does not eliminate contaminants entering the tank. Tank cleaning, filtration and UV remain the recommended treatment chain.",
  },

  "services/catchment-upgrade": {
    kind: "service",
    name: "Catchment Upgrade",
    tagline:
      "Improvements at the roof / gutter / downpipe stage to reduce contaminants entering the tank.",
    hero_image: PWS_IMG("2017/04/Rainwater-Harvesting-off-Commercial-Building.jpg"),
    what_it_does: [
      "Audits the catchment chain — roof, gutters, leaf guards, downpipes, tank inlet.",
      "Recommends practical upgrades such as gutter guards, leaf strainers and first-flush diverters.",
      "Helps reduce the contaminant load reaching the tank, supporting longer cycles between QuadClean services.",
    ],
    best_for: [
      "Properties under heavy tree cover",
      "Homes where catchment-side debris is the root cause of tank-water issues",
      "Owners wanting a long-term reduction in sediment accumulation",
    ],
    included: [
      "Onsite catchment inspection",
      "Recommendations report",
      "Quoted upgrade items (first flush diverters, strainer kits, etc.)",
    ],
    benefits: [
      "Supports better water quality before the water reaches the tank",
      "Reduces sediment build-up — extending the practical service interval",
      "Pairs with QuadClean tank cleaning for end-to-end source-side hygiene",
    ],
    maintenance: [
      "Annual visual check of gutters, leaf guards and first-flush diverters.",
    ],
    related: [
      { label: "First flush diverter", to: "/services/first-flush-diverter" },
      { label: "QuadClean tank cleaning", to: "/services/tank-cleaning" },
    ],
    disclaimer:
      "Catchment upgrades reduce but do not eliminate contamination risk. Tank cleaning, filtration and UV remain the recommended treatment chain where bacteria has been detected.",
  },
};

export function getPage(slugPath) {
  // slugPath e.g. "products/tr20uv" or "/products/tr20uv"
  const clean = (slugPath || "").replace(/^\//, "").replace(/\/+$/, "");
  return PAGES[clean] || null;
}

export const ALL_PAGE_PATHS = Object.keys(PAGES).map((p) => `/${p}`);
export default PAGES;
